import type { TextNormalization } from '../model/canonical.ts';

/**
 * Makes a typed answer and an accepted answer comparable.
 *
 * Each step is opt-in from the content, because what counts as the "same answer" is a property of
 * the question. Ignoring case is almost always right; ignoring punctuation is right for prose and
 * wrong for a command line, where `ip route` and `iproute` are not the same thing.
 *
 * Deliberately no regular expressions anywhere near this: a pattern supplied by an untrusted file
 * can hang the tab, which would make the exam format executable by another name.
 */
export function normalizeAnswer(value: string, options: TextNormalization): string {
  let result = value;

  if (options.trimWhitespace) {
    result = result.trim();
  }
  if (options.collapseWhitespace) {
    result = result.replace(/\s+/g, ' ');
  }
  if (options.ignoreCase) {
    // Locale-independent on purpose: a Turkish locale lower-cases "I" to a dotless ı, which would
    // make the same answer match or not depending on where the candidate happens to be.
    result = result.toLowerCase();
  }
  if (options.ignoreAccents) {
    result = result.normalize('NFD').replace(/\p{Diacritic}/gu, '');
  }
  if (options.ignorePunctuation) {
    result = result.replace(/[\p{P}\p{S}]/gu, '');
    // Removing punctuation can leave the gaps it used to fill, so collapse again if asked to.
    if (options.collapseWhitespace) {
      result = result.replace(/\s+/g, ' ').trim();
    }
  }

  return result;
}

export function matchesAccepted(
  typed: string,
  accepted: readonly string[],
  options: TextNormalization,
): boolean {
  const normalized = normalizeAnswer(typed, options);
  return accepted.some((candidate) => normalizeAnswer(candidate, options) === normalized);
}
